<script setup lang="ts">
import { computed, nextTick, onUnmounted, useTemplateRef, watch } from "vue";
import { useRoute } from "vue-router";

import { boardActions } from "@/action/boardActions";
import type { Action } from "@/action/types";
import IconButton from "@/components/ui/IconButton/IconButton.vue";
import { useShortcuts } from "@/composables/useShortcuts";
import { defaultCardSizeFactor, maxCardSizeFactor, minCardSizeFactor } from "@/model/Settings";
import { isBacklogBoard, isFrontendBoard } from "@/model/board";
import { useBoardStore } from "@/store/board";
import { useToastStore } from "@/store/toast";
import { useUserStore } from "@/store/user";
import { useZoomStore } from "@/store/zoom";

import ZoomPopout from "./ZoomPopout.vue";

const actions = boardActions;
const cardZooming = computed({
  get: () => useBoardStore().scaleDropdownOpen,
  set: (value) => useBoardStore().setScaleDropdownOpen(value),
});
const zoomPopoutRef = useTemplateRef<InstanceType<typeof ZoomPopout>>("zoomPopout");
const toggleZoomRef = useTemplateRef("toggleZoom");

let cardZoomTimeout: number | undefined;
let lastCardZoom = 0;

onUnmounted(() => (cardZooming.value = false));

// When the app-zoom popup opens, close this one because they visually overlap
watch(
  () => useZoomStore().dropdownOpen,
  (open) => {
    if (open && cardZooming.value) {
      cardZooming.value = false;
    }
  },
);

const canNonTeamZoom = computed(() => useUserStore().isAllowed("nonTeamZoom"));
const cardZoomable = computed(() => {
  const board = useBoardStore().board;
  return (
    board &&
    !isBacklogBoard(board.type) &&
    !isFrontendBoard(board.type) &&
    useUserStore().isAllowed("edit") &&
    useUserStore().isNonTeamZoomAllowed(board)
  );
});
const shortcuts = computed(
  () =>
    boardActions.smallerCardSize.data.shortcut.name() +
    " " +
    boardActions.biggerCardSize.data.shortcut.name(),
);

const cardSizeFactor = computed(
  () => useBoardStore().board?.cardSize.factor ?? defaultCardSizeFactor,
);
const zoomOutDisabled = computed(() => cardSizeFactor.value <= minCardSizeFactor);
const zoomInDisabled = computed(() => cardSizeFactor.value >= maxCardSizeFactor);

const route = useRoute();

watch(route, () => (cardZooming.value = false), { immediate: true });

const { registerShortcut } = useShortcuts();
registerShortcut(boardActions.smallerCardSize, { repeat: true });
registerShortcut(boardActions.biggerCardSize, { repeat: true });

function cardZoom(action?: Action, rep?: boolean) {
  // Cancel any previous zoom action
  clearTimeout(cardZoomTimeout);

  if (action) {
    const delay = rep ? Math.max(0, 75 + lastCardZoom - performance.now()) : 200;
    lastCardZoom = performance.now();
    void action("board-actions-menu");
    cardZoomTimeout = window.setTimeout(() => cardZoom(action, true), delay);
  }
}

function toggleCardZooming() {
  if (cardZoomable.value) {
    cardZooming.value = !cardZooming.value;
    if (cardZooming.value) {
      void nextTick(() => void zoomPopoutRef.value?.focusZoomIn());
    } else {
      toggleZoomRef.value?.focus();
    }
  } else if (!useUserStore().isNonTeamZoomAllowed(useBoardStore().currentBoard())) {
    useToastStore().show(/*$t*/ "message.onlyRTEAdminAllowed");
  }
}

function close() {
  cardZooming.value = false;
  toggleZoomRef.value?.focus();
}
</script>

<!-- eslint-disable vuejs-accessibility/no-static-element-interactions-->
<template>
  <div
    v-if="cardZoomable || canNonTeamZoom"
    class="card-zoom"
    :class="{ disabled: !cardZoomable }"
    @keydown.esc="close"
  >
    <!-- eslint-enable vuejs-accessibility/no-static-element-interactions-->
    <IconButton
      ref="toggleZoom"
      icon="general/scale"
      :text="$t('action.scaleStickies')"
      :activated="cardZooming"
      :disabled="!cardZoomable"
      :shortcut="shortcuts"
      @click="toggleCardZooming"
    />
    <Transition name="fade">
      <ZoomPopout
        v-if="cardZooming"
        ref="zoomPopout"
        class="popout"
        role="dialog"
        aria-modal="false"
        :aria-label="$t('action.scaleStickies')"
        :zoom-out-disabled
        :zoom-in-disabled
        :zoom-out-label="$t('label.scaleStickies.smaller')"
        :zoom-in-label="$t('label.scaleStickies.bigger')"
        @click.stop
        @zoom-out-start="cardZoom(actions.smallerCardSize)"
        @zoom-in-start="cardZoom(actions.biggerCardSize)"
        @zoom-end="cardZoom()"
        @close="close"
      />
    </Transition>
  </div>
</template>

<style lang="scss" scoped>
.card-zoom {
  position: relative;
  display: flex;
}
</style>
